import { z } from 'zod';
import { dataEhAnterior, formatarDataCurta, partesDaData, somarDias, somarMeses } from './datas.js';

/** Sinal de 5% (Carta de Intenções) ou saldo de 95% (Termo de Aceite). */
export const TIPOS_COBRANCA = ['sinal', 'saldo'] as const;
export const tipoCobrancaSchema = z.enum(TIPOS_COBRANCA);
export type TipoCobranca = z.infer<typeof tipoCobrancaSchema>;

/**
 * Formas de pagamento aceitas pelo Asaas que usamos.
 * `UNDEFINED` deixa a escolha (Pix, cartão ou boleto) com o pagador na própria fatura.
 */
export const FORMAS_PAGAMENTO = ['UNDEFINED', 'PIX', 'CREDIT_CARD', 'BOLETO'] as const;
export const formaPagamentoSchema = z.enum(FORMAS_PAGAMENTO);
export type FormaPagamento = z.infer<typeof formaPagamentoSchema>;

/**
 * Modalidade do link, escolhida pelo **operador** na hora de gerar.
 *
 *  `avista`     — pagamento único, por cobrança avulsa: cartão, Pix **ou boleto**.
 *  `parcelado`  — checkout com cartão **ou Pix**. Parcelar é só no cartão, e quem escolhe
 *                 **quantas** parcelas (1 a 12) é o pagador, na tela. O operador libera o
 *                 parcelamento; não o define. Sem boleto, porque o `/checkouts` do Asaas
 *                 não o aceita — ver `criarCheckout`.
 *  `carne`      — boleto parcelado. Existe porque o parcelamento no checkout é só cartão, e
 *                 boleto é meio corrente numa venda de seis dígitos para empresa.
 *
 * **`carne` não é `parcelado` com outro meio de pagamento — é outro produto.** Nele quem
 * fixa a quantidade de parcelas é o **operador**, na negociação, e o Asaas emite **uma
 * cobrança por parcela**, com vencimentos mensais. Cada parcela pode vencer sem ser paga, o
 * dinheiro entra ao longo de meses em vez de agora, e a cadeira só é dada por quitada
 * quando a última for confirmada — ver `carneQuitado`.
 *
 * O recorte segue o Termo, cl. 3.2. Pix parcelado não existe no instrumento nem no
 * gateway — liberar o parcelamento não impede ninguém de quitar de uma vez por Pix no
 * mesmo link.
 */
export const MODALIDADES_COBRANCA = ['avista', 'parcelado', 'carne'] as const;
export const modalidadeCobrancaSchema = z.enum(MODALIDADES_COBRANCA);
export type ModalidadeCobranca = z.infer<typeof modalidadeCobrancaSchema>;

/** Rótulo da modalidade para tela, trilha e mensagem de recusa. */
export const ROTULOS_MODALIDADE: Record<ModalidadeCobranca, string> = {
  avista: 'à vista',
  parcelado: 'parcelado no cartão',
  carne: 'carnê de boletos',
};

/**
 * Situações da cobrança. Espelham os status do Asaas que nos interessam; qualquer
 * outro status recebido pelo webhook é gravado como `OUTRO` e registrado no log.
 */
export const SITUACOES_COBRANCA = [
  'PENDENTE',
  'CONFIRMADA',
  'RECEBIDA',
  'VENCIDA',
  'ESTORNADA',
  'CANCELADA',
  'OUTRO',
] as const;
export const situacaoCobrancaSchema = z.enum(SITUACOES_COBRANCA);
export type SituacaoCobranca = z.infer<typeof situacaoCobrancaSchema>;

export const cobrancaSchema = z.object({
  cobrancaId: z.string(),
  candidatoId: z.string(),
  tipo: tipoCobrancaSchema,
  /**
   * id do checkout no Asaas. É ele que existe no instante em que o link é gerado, e é por
   * ele que o webhook reencontra esta cobrança — a cobrança propriamente dita (`pay_xxx`)
   * só nasce quando o pagador conclui o pagamento.
   */
  asaasCheckoutId: z.string().optional(),
  /**
   * id da cobrança no Asaas (pay_xxx). Ausente enquanto ninguém pagou.
   *
   * Cobranças emitidas antes da migração para o checkout têm este campo preenchido desde a
   * criação e **não** têm `asaasCheckoutId` — daí ele ser opcional dos dois lados.
   */
  asaasId: z.string().optional(),
  /**
   * id do **plano de parcelamento** no Asaas, só no carnê.
   *
   * É por ele que o webhook reencontra a cobrança, e não pelo `asaasId`: cada parcela paga
   * chega com um `pay_xxx` diferente, e guardamos apenas o da primeira. Sem este campo, da
   * segunda parcela em diante o evento cairia como "cobrança desconhecida" e o carnê nunca
   * seria dado por quitado.
   */
  asaasInstallmentId: z.string().optional(),
  /** id do cliente no Asaas (cus_xxx). */
  asaasClienteId: z.string(),
  /** Valor bruto em centavos. */
  valor: z.number().int().nonnegative(),
  /**
   * Parcela que sai da conta titular via split, em centavos. A conta é da RREVELA,
   * então esta é a parte da Academia (70%). Ver packages/shared/src/split.ts.
   */
  valorSplitAcademia: z.number().int().nonnegative(),
  /** O que o link **ofereceu** — ver `formaDaModalidade`. */
  formaPagamento: formaPagamentoSchema,
  modalidade: modalidadeCobrancaSchema,
  /** Teto de parcelas oferecido: 1 à vista, `MAX_PARCELAS_SALDO` parcelado, o combinado no carnê. */
  maxParcelas: z.number().int().positive(),
  /**
   * Quantas parcelas esta cobrança tem.
   *
   * No `parcelado` quem escolhe é o pagador, então só se sabe **depois** do pagamento, e só
   * se o gateway informar. No `carne` quem escolhe é o operador, então já vem preenchido na
   * emissão — é ele que diz quantos boletos precisam ser pagos para o saldo estar quitado.
   */
  parcelas: z.number().int().positive().optional(),
  /**
   * Parcelas do carnê já confirmadas: id da cobrança no Asaas → valor líquido em centavos.
   *
   * Um mapa, e não um contador, porque o mesmo pagamento chega mais de uma vez — o boleto
   * dispara `PAYMENT_CONFIRMED` e depois `PAYMENT_RECEIVED`, os dois "pagamento efetivado".
   * Somar cegamente daria 12 de 6 parcelas e quitaria o saldo pela metade do dinheiro;
   * gravado por id, o segundo evento não muda nada.
   */
  parcelasRecebidas: z.record(z.string(), z.number().int().nonnegative()).optional(),
  vencimento: z.string(), // AAAA-MM-DD
  situacao: situacaoCobrancaSchema,
  /** URL da fatura para enviar ao pagador. */
  urlFatura: z.string().url(),
  /** Valor efetivamente recebido, em centavos — preenchido pelo webhook. */
  valorRecebido: z.number().int().nonnegative().optional(),
  pagoEm: z.string().datetime().optional(),
  criadoEm: z.string().datetime(),
  criadoPor: z.string(),
  atualizadoEm: z.string().datetime(),
});
export type Cobranca = z.infer<typeof cobrancaSchema>;

export const MAX_PARCELAS_SALDO = 12;
/** Menos de duas parcelas não é parcelamento — é uma cobrança à vista com outro nome. */
export const MIN_PARCELAS_CARNE = 2;

/** Corpo aceito por POST /candidatos/:id/cobrancas */
export const criarCobrancaSchema = z
  .object({
    tipo: tipoCobrancaSchema,
    /**
     * Sinal é sempre à vista (Carta, cl. 3.2) — a API rejeita parcelamento no sinal.
     * No saldo, `parcelado` libera de 1x a `MAX_PARCELAS_SALDO` no cartão e é o pagador
     * quem escolhe a quantidade; `carne` emite boletos na quantidade que o operador
     * fixar (Termo, cl. 3.2).
     */
    modalidade: modalidadeCobrancaSchema.default('avista'),
    /**
     * Quantidade de boletos do carnê. Obrigatória nele e recusada nas outras modalidades:
     * no `parcelado` quem decide é o pagador, e aceitar o campo ali daria ao operador a
     * impressão de estar fixando algo que o checkout ignora.
     */
    parcelas: z.number().int().min(MIN_PARCELAS_CARNE).max(MAX_PARCELAS_SALDO).optional(),
    /**
     * Vencimento do **primeiro** boleto do carnê, em AAAA-MM-DD. Os seguintes caem no mesmo
     * dia do mês — ver `vencimentosDoCarne`.
     *
     * Opcional: sem ela vale o prazo contratual do saldo, `DIAS_VENCIMENTO_SALDO` dias a
     * partir de hoje. Existe porque uma venda parcelada se negocia com data — "a primeira
     * fica para o dia 10, quando entra o faturamento" —, e o prazo de sete dias não cabe
     * nessa conversa.
     */
    primeiroVencimento: z.string().optional(),
  })
  .strict()
  .superRefine((pedido, ctx) => {
    if (pedido.modalidade === 'carne' && pedido.parcelas === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['parcelas'],
        message: `Informe de ${MIN_PARCELAS_CARNE} a ${MAX_PARCELAS_SALDO} parcelas para o carnê.`,
      });
    }
    if (pedido.modalidade !== 'carne' && pedido.parcelas !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['parcelas'],
        message: 'Só o carnê tem quantidade de parcelas definida pelo operador.',
      });
    }
    /*
     * O parcelado não tem data a escolher: o checkout expira em 24h e a cobrança só nasce
     * quando o pagador conclui. Aceitar o campo ali daria ao operador a impressão de estar
     * marcando um vencimento que ninguém vai honrar.
     */
    if (pedido.modalidade !== 'carne' && pedido.primeiroVencimento !== undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['primeiroVencimento'],
        message: 'Só o carnê tem data de primeiro vencimento escolhida pelo operador.',
      });
    }
  });
export type CriarCobranca = z.infer<typeof criarCobrancaSchema>;

/**
 * Quantas parcelas o link comporta. No carnê é o que o operador fixou; no parcelado, o teto
 * que o pagador pode escolher; à vista, uma.
 */
export function maxParcelasDaModalidade(modalidade: ModalidadeCobranca, parcelas?: number): number {
  if (modalidade === 'carne') return parcelas ?? MIN_PARCELAS_CARNE;
  return modalidade === 'parcelado' ? MAX_PARCELAS_SALDO : 1;
}

/**
 * Até quando o operador pode empurrar o primeiro vencimento.
 *
 * Não é limite do gateway: é o que impede um ano digitado errado — "2036" no lugar de
 * "2026" — de virar um carnê que só termina na década seguinte, descoberto meses depois.
 */
export const MAX_DIAS_PRIMEIRO_VENCIMENTO = 180;

export interface VencimentoInvalido {
  motivo: string;
}

/**
 * `null` quando a data do primeiro vencimento é aceitável. `hoje` vem de `hojeNaOperacao`.
 */
export function validarPrimeiroVencimento(data: string, hoje: string): VencimentoInvalido | null {
  if (!partesDaData(data)) {
    return { motivo: 'Informe uma data de calendário válida, no formato AAAA-MM-DD.' };
  }
  if (dataEhAnterior(data, hoje)) {
    return { motivo: 'O primeiro vencimento não pode ser anterior a hoje.' };
  }

  const teto = somarDias(hoje, MAX_DIAS_PRIMEIRO_VENCIMENTO);
  if (dataEhAnterior(teto, data)) {
    return {
      motivo:
        `O primeiro vencimento não pode passar de ${formatarDataCurta(teto)} ` +
        `(${MAX_DIAS_PRIMEIRO_VENCIMENTO} dias). Confira o ano.`,
    };
  }

  return null;
}

/**
 * As datas de todas as parcelas do carnê, a partir do primeiro vencimento.
 *
 * Todas caem no **mesmo dia do mês** do primeiro — dia 5 é dia 5 em todos os meses —, com
 * o encurtamento para o último dia quando ele não existe (dia 31 vence em 28 de fevereiro).
 * Ver `somarMeses`: cada parcela é contada a partir do primeiro vencimento, nunca da
 * anterior, senão um único fevereiro empurraria o dia de toda a série para sempre.
 *
 * Serve para o cockpit mostrar as datas antes de gerar, e é a mesma conta que o Asaas faz
 * ao criar o parcelamento — quem emite os boletos é ele, a partir da data da primeira.
 */
export function vencimentosDoCarne(primeiro: string, parcelas: number): string[] {
  return Array.from({ length: parcelas }, (_, i) => somarMeses(primeiro, i));
}

export interface ResumoDoCarne {
  /** Parcelas já confirmadas pelo gateway. */
  pagas: number;
  /** Total combinado com o operador. */
  total: number;
  /** Soma do líquido recebido, em centavos. */
  recebido: number;
  quitado: boolean;
}

/**
 * Onde o carnê está.
 *
 * A conta mora aqui, e não no webhook, porque o cockpit precisa dela para mostrar "3 de 12
 * pagas" sem repetir a regra — e porque é ela que decide se a cadeira pode ser dada por
 * paga. Um carnê de 12 boletos com o primeiro liquidado tem 1/12 do dinheiro; tratar isso
 * como saldo quitado entregaria a cadeira contra R$ 28.500 de R$ 342.000.
 */
export function resumoDoCarne(
  cobranca: Pick<Cobranca, 'parcelas' | 'parcelasRecebidas'>,
): ResumoDoCarne {
  const recebidas = Object.values(cobranca.parcelasRecebidas ?? {});
  const total = cobranca.parcelas ?? 0;
  const pagas = recebidas.length;

  return {
    pagas,
    total,
    recebido: recebidas.reduce((soma, v) => soma + v, 0),
    quitado: total > 0 && pagas >= total,
  };
}

/** O saldo do carnê só está pago quando **todas** as parcelas foram confirmadas. */
export function carneQuitado(cobranca: Pick<Cobranca, 'parcelas' | 'parcelasRecebidas'>): boolean {
  return resumoDoCarne(cobranca).quitado;
}

/**
 * O que o link oferece ao pagador.
 *
 * O cardápio é propriedade do endpoint, não deste campo: à vista sai por `/payments`
 * (cartão, Pix e boleto) e parcelado por `/checkouts` (cartão e Pix). No carnê pedimos
 * `BOLETO` — é o que faz o Asaas emitir o carnê em vez de deixar o pagador escolher cartão
 * e redefinir as parcelas —, mas a fatura de cada parcela ainda oferece **Pix** como
 * alternativa ao boleto daquele mês. Verificado no sandbox.
 *
 * Cobranças gravadas antes de o Pix entrar no parcelado guardam `CREDIT_CARD`, e continuam
 * certas: aqueles links ofereciam mesmo só cartão.
 */
export function formaDaModalidade(modalidade: ModalidadeCobranca): FormaPagamento {
  return modalidade === 'carne' ? 'BOLETO' : 'UNDEFINED';
}

/**
 * Situações em que a cobrança ainda "vale": ou está esperando pagamento, ou já foi paga.
 * Existindo uma dessas, emitir outra do mesmo tipo mandaria dois links de seis dígitos
 * para o mesmo pagador.
 */
export const SITUACOES_EM_ABERTO: readonly SituacaoCobranca[] = [
  'PENDENTE',
  'CONFIRMADA',
  'RECEBIDA',
];

export function cobrancaEmAberto(cobranca: Pick<Cobranca, 'situacao'>): boolean {
  return SITUACOES_EM_ABERTO.includes(cobranca.situacao);
}

export function cobrancaJaPaga(cobranca: Pick<Cobranca, 'situacao'>): boolean {
  return cobranca.situacao === 'CONFIRMADA' || cobranca.situacao === 'RECEBIDA';
}
